import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addDoc, collection } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  ArrowLeft,
  CheckCircle2,
  CircleAlert,
  Eraser,
  Keyboard,
  Loader2,
  PenLine,
  RotateCcw,
  Send,
  XCircle
} from 'lucide-react';
import BackHomeButton from '../components/BackHomeButton';
import { auth, db, functions } from '../firebase';

const ANSWER_HEADING_PATTERN = /(^|\n)\s*[#>*_\s-]*(?:answers?|answer\s+(?:key|section|sheet)|solutions?|solution\s+key)\s*[:.)-]?\s*[#>*_\s-]*(?=\n|$)/i;
const QUESTION_START_PATTERN = /^\s*(?:q(?:uestion)?\.?\s*)?(\d{1,3})\s*[).:-]\s*(.+)?$/i;
const ANSWER_START_PATTERN = /^\s*(?:ans(?:wer)?\.?\s*)?(?:q(?:uestion)?\.?\s*)?(\d{1,3})\s*[).:-]\s*(.+)?$/i;
function readCachedPayload() {
  if (typeof window === 'undefined') return {};

  try {
    return JSON.parse(sessionStorage.getItem('solveWorksheetOnlinePayload') || '{}') || {};
  } catch (err) {
    console.error('Unable to read cached worksheet:', err);
    return {};
  }
}

function splitWorksheet(rawText = '') {
  const normalized = rawText.replace(/\r\n/g, '\n').trim();
  const match = ANSWER_HEADING_PATTERN.exec(normalized);

  if (!match) {
    return {
      questionText: normalized,
      answerText: ''
    };
  }

  return {
    questionText: normalized.slice(0, match.index).trim(),
    answerText: normalized.slice(match.index).trim()
  };
}

function cleanAnswerValue(value = '') {
  let cleaned = value
    .toString()
    .replace(/^[-:]\s*/, '')
    .trim();
  const markerPattern = /\b(?:ans(?:wer)?|correct\s+answer)\s*[:.-]\s*/gi;
  let lastMarkerEnd = -1;

  while (markerPattern.exec(cleaned)) {
    lastMarkerEnd = markerPattern.lastIndex;
  }

  if (lastMarkerEnd >= 0) {
    cleaned = cleaned.slice(lastMarkerEnd).trim();
  }

  return cleaned
    .replace(/^(?:ans(?:wer)?\.?)\s*[:.-]?\s*/i, '')
    .replace(/^[-:]\s*/, '')
    .trim();
}

const DEFAULT_SECTION_TITLE = 'General';
const DEFAULT_SECTION_KEY = '__general__';

function stripMarkdown(value = '') {
  return value
    .replace(/^\s*#{1,6}\s*/, '')
    .replace(/^\s*[-*]\s+/, '')
    .replace(/^\s*(?:\*\*|__)+/, '')
    .replace(/(?:\*\*|__)+\s*$/, '')
    .replace(/[`*_]/g, '')
    .replace(/\s*[:\uFF1A]\s*$/, '')
    .trim();
}

function normalizeSectionKey(value = '') {
  return stripMarkdown(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(?:the|section|questions?|answers?)\b/g, ' ')
    .replace(/\bblanks\b/g, 'blank')
    .replace(/\bsentences\b/g, 'sentence')
    .replace(/\bwords\b/g, 'word')
    .replace(/\s+/g, ' ')
    .trim() || DEFAULT_SECTION_KEY;
}

function isSectionHeadingLine(line = '') {
  const raw = line.trim();

  if (!raw) return '';
  if (/^\s*[a-z]\s*[).]\s+.+/i.test(raw)) return '';
  if (raw.match(QUESTION_START_PATTERN) || raw.match(ANSWER_START_PATTERN) || raw.match(/^\s*\d{1,3}\s+.+/)) {
    return '';
  }

  const heading = stripMarkdown(raw);
  const wordCount = heading.split(/\s+/).filter(Boolean).length;
  const hasHeaderMarker = /^\s*(?:#{1,6}\s+|\*\*.+\*\*|__.+__)/.test(raw) || /[:\uFF1A]\s*$/.test(raw);
  const looksLikeQuestionType = /\b(?:fill|blank|complete|sentence|match|following|follwoing|column|jumbled|scrambled|unscramble|word|one\s+word|answer\s+in|true|false|choose|mcq|multiple\s+choice|short|long|application|skill|based|lesson|questions?|tick|write|circle|identify|name\s+the|define|correct|incorrect|explain|describe)\b/i.test(heading);

  if (!heading || heading.length > 90 || /[.!?]$/.test(heading)) return '';
  if (!looksLikeQuestionType) return '';
  if (hasHeaderMarker || wordCount <= 7) return heading;

  return '';
}

function getQuestionBody(value = '') {
  return value
    .replace(/^\s*(?:q(?:uestion)?\.?\s*)?\d{1,3}\s*[).:-]\s*/i, '')
    .replace(/^\s*\d{1,3}\s+/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function isMatchTheFollowingText(value = '') {
  return /\bmatch\s+(?:the\s+)?(?:following|follwoing|columns?|items?)\b/i.test(stripMarkdown(value));
}

function parseSectionItems(text = '', mode = 'question') {
  const source = mode === 'answer' ? text.replace(ANSWER_HEADING_PATTERN, '\n') : text;
  const lines = source.split('\n');
  const items = [];
  let sectionTitle = DEFAULT_SECTION_TITLE;
  let sectionKey = DEFAULT_SECTION_KEY;
  let current = null;

  const pushCurrent = () => {
    if (!current) return;

    if (mode === 'answer') {
      const answer = cleanAnswerValue(current.lines.join(' '));

      if (answer) {
        items.push({
          number: current.number,
          sectionTitle: current.sectionTitle,
          sectionKey: current.sectionKey,
          answer
        });
      }
    } else {
      const question = current.lines.join('\n').trim();

      if (getQuestionBody(question)) {
        items.push({
          number: current.number,
          sectionTitle: current.sectionTitle,
          sectionKey: current.sectionKey,
          question
        });
      }
    }

    current = null;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) return;

    const heading = isSectionHeadingLine(trimmed);

    if (heading) {
      pushCurrent();
      sectionTitle = heading;
      sectionKey = normalizeSectionKey(heading);
      return;
    }

    const numberedMatch =
      trimmed.match(mode === 'answer' ? ANSWER_START_PATTERN : QUESTION_START_PATTERN) ||
      trimmed.match(/^\s*(\d{1,3})\s+(.+)$/);

    if (numberedMatch) {
      pushCurrent();
      current = {
        number: Number(numberedMatch[1]),
        sectionTitle,
        sectionKey,
        lines: []
      };

      if (numberedMatch[2]) {
        current.lines.push(mode === 'answer' ? numberedMatch[2].trim() : trimmed);
      }

      return;
    }

    if (current) {
      current.lines.push(trimmed);
    }
  });

  pushCurrent();
  return items;
}

function parseAnswerKey(answerText = '') {
  return parseSectionItems(answerText, 'answer');
}

function parseQuestionItems(questionText = '') {
  return parseSectionItems(questionText, 'question');
}

function getSectionNumberKey(item) {
  return `${item.sectionKey || DEFAULT_SECTION_KEY}::${item.number}`;
}

function getQuestionId(question, index = 0) {
  return `question-${index + 1}-${(question.sectionKey || DEFAULT_SECTION_KEY).replace(/\s+/g, '-')}-${question.number}`;
}

function buildWorksheet(rawText = '') {
  const split = splitWorksheet(rawText);
  const rawAnswers = parseAnswerKey(split.answerText).filter(
    (answer) => !isMatchTheFollowingText(answer.sectionTitle) && !isMatchTheFollowingText(answer.answer)
  );
  const answerBySectionNumber = new Map();
  const answerNumberCounts = new Map();
  const uniqueAnswerByNumber = new Map();

  rawAnswers.forEach((answer) => {
    const sectionNumberKey = getSectionNumberKey(answer);

    if (!answerBySectionNumber.has(sectionNumberKey)) {
      answerBySectionNumber.set(sectionNumberKey, answer);
    }

    answerNumberCounts.set(answer.number, (answerNumberCounts.get(answer.number) || 0) + 1);
    uniqueAnswerByNumber.set(answer.number, answer);
  });

  const questions = parseQuestionItems(split.questionText)
    .filter((question) => getQuestionBody(question.question))
    .filter((question) => !isMatchTheFollowingText(question.sectionTitle) && !isMatchTheFollowingText(question.question))
    .map((question, index) => {
      const matchedAnswer =
        answerBySectionNumber.get(getSectionNumberKey(question)) ||
        (answerNumberCounts.get(question.number) === 1 ? uniqueAnswerByNumber.get(question.number) : null);

      return {
        id: getQuestionId(question, index),
        number: question.number,
        sectionTitle: question.sectionTitle,
        sectionKey: question.sectionKey,
        question: question.question,
        expectedAnswer: matchedAnswer?.answer || ''
      };
    });

  return {
    questionText: split.questionText,
    hasAnswerKey: questions.some((question) => question.expectedAnswer),
    questions
  };
}

function getDataUrlPayload(dataUrl = '') {
  const match = dataUrl.match(/^data:(.*?);base64,(.*)$/);

  if (!match) {
    return {
      mimeType: 'image/png',
      data: ''
    };
  }

  return {
    mimeType: match[1] || 'image/png',
    data: match[2] || ''
  };
}

function normalizeGeminiStatus(value = '') {
  const status = value.toString().toLowerCase();

  if (status.includes('incorrect') || status.includes('wrong')) return 'incorrect';
  if (status.includes('partial') || status.includes('close') || status.includes('incomplete')) return 'partial';
  if (status.includes('review') || status.includes('unreadable') || status.includes('unclear')) return 'review';
  if (status.includes('correct') || status.includes('right')) return 'correct';

  return 'review';
}

function getResultLabel(status) {
  const labels = {
    correct: 'Correct',
    partial: 'Partially correct',
    incorrect: 'Incorrect Answer try again',
    review: 'Needs review',
    empty: 'Not answered'
  };

  return labels[status] || labels.review;
}

function cleanGeminiFeedback(value = '') {
  return value
    .toString()
    .replace(/(?:the\s+)?correct\s+answer\s*(?:is|:).*$/i, '')
    .trim();
}

function normalizeKeyAnswer(value = '') {
  return value
    .toString()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/(?<=\d),(?=\d)/g, '')
    .replace(/[^\p{L}\p{N}\s.%/-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getAnswerKeyOptions(expectedAnswer = '') {
  return expectedAnswer
    .split(/\s*(?:\/|;|\bor\b)\s*/i)
    .map((option) => option.trim())
    .filter(Boolean);
}

function isTypedAnswerKeyMatch(userAnswer = '', expectedAnswer = '') {
  const normalizedUser = normalizeKeyAnswer(userAnswer);
  const normalizedExpected = normalizeKeyAnswer(expectedAnswer);

  if (!normalizedUser || !normalizedExpected) return false;
  if (normalizedUser === normalizedExpected) return true;

  return getAnswerKeyOptions(expectedAnswer).some((option) => {
    const normalizedOption = normalizeKeyAnswer(option);
    return normalizedOption && normalizedUser === normalizedOption;
  });
}

function parseGeminiBatchResults(text = '') {
  const cleaned = text
    .replace(/```json/gi, '')
    .replace(/```/g, '')
    .trim();
  const jsonMatch = cleaned.match(/\{[\s\S]*\}/);

  if (!jsonMatch) {
    return {};
  }

  try {
    const parsed = JSON.parse(jsonMatch[0]);
    const rows = Array.isArray(parsed) ? parsed : parsed.results || parsed.answers || [];

    return rows.reduce((acc, row) => {
      const id = row.id || (row.questionNumber ? `question-${row.questionNumber}` : '');

      if (!id) return acc;

      acc[id] = {
        status: normalizeGeminiStatus(row.status || row.result || row.verdict),
        matchPercent: Number(row.matchPercent || row.match_percentage || row.score || 0),
        feedback: cleanGeminiFeedback(row.feedback || row.reason || row.comment || '')
      };
      return acc;
    }, {});
  } catch (err) {
    console.error('Unable to parse Gemini answer check:', err);
    return {};
  }
}

function buildGeminiCheckParts(items) {
  const promptItems = items.map((item) => ({
    id: item.id,
    questionNumber: item.number,
    displayNumber: item.displayNumber,
    questionType: item.sectionTitle || '',
    question: item.question,
    answerKey: item.expectedAnswer,
    typedAnswer: item.typedAnswer || '',
    handwrittenAnswerImage: item.drawing ? `image_for_${item.id}` : ''
  }));
  const prompt = `You are checking student worksheet answers. Compare each student answer with the hidden answer key.

Accept answers that are semantically correct even when wording, capitalization, spelling, grammar, or order is different. Mark partial when the answer has some key idea but is incomplete. Mark incorrect only when the answer is wrong or missing important meaning. Use review only when a handwritten answer is unreadable.

Do not reveal the answer key or the correct answer in feedback. Keep feedback short and tell the student what is missing or wrong without giving away the full answer.

Return JSON only in this exact shape, using the exact id values from the input:
{"results":[{"id":"question-fill-blank-1","status":"correct|partial|incorrect|review","matchPercent":0-100,"feedback":"short feedback"}]}

Use matchPercent to show how much of the answer key meaning is covered by the student answer. If the answer covers at least 75% of the required meaning but is not fully complete, use status "partial" with matchPercent 75 or higher.

Questions to check:
${JSON.stringify(promptItems, null, 2)}`;
  const parts = [{ text: prompt }];

  items.forEach((item) => {
    if (!item.drawing) return;

    const image = getDataUrlPayload(item.drawing);

    if (!image.data) return;

    parts.push({ text: `Handwritten answer image for ${item.id}:` });
    parts.push({
      inlineData: {
        mimeType: image.mimeType,
        data: image.data
      }
    });
  });

  return parts;
}

async function checkAnswersWithGemini(items) {
  const callGemini = httpsCallable(functions, 'callGemini');
  const result = await callGemini({
    contents: [
      {
        role: 'user',
        parts: buildGeminiCheckParts(items)
      }
    ],
    generationConfig: {
      maxOutputTokens: Math.min(8192, Math.max(1024, items.length * 220)),
      temperature: 0.1
    }
  });
  const text = result.data?.candidates?.[0]?.content?.parts?.[0]?.text || '';

  return parseGeminiBatchResults(text);
}

function getStudentAnswerSummary(item, typedAnswers, drawings, answerModes) {
  const typedAnswer = (typedAnswers[item.id] || '').trim();
  const drawing = drawings[item.id] || '';
  const answerMode = answerModes[item.id] || 'type';

  return {
    typedAnswer,
    drawing,
    answerMode,
    hasAnswer: Boolean(typedAnswer || drawing),
    displayAnswer: typedAnswer || (drawing ? '[Handwritten answer]' : '')
  };
}

function buildFinalQuestionResults({
  questions,
  checkedResults,
  typedAnswers,
  drawings,
  answerModes,
  revealedAnswers,
  submittedAnswers = {}
}) {
  return questions.map((item, index) => {
  const isSubmitted = Boolean(submittedAnswers[item.id]);
  const isRevealed = Boolean(revealedAnswers[item.id]);
  const shouldGrade = isSubmitted || isRevealed;
  const studentAnswer = isSubmitted
    ? getStudentAnswerSummary(item, typedAnswers, drawings, answerModes)
    : {
        typedAnswer: '',
        drawing: '',
        answerMode: answerModes[item.id] || 'type',
        hasAnswer: false,
        displayAnswer: ''
      };
  const checked = checkedResults[item.id] || {};
    const answerKeyFeedback = item.expectedAnswer
      ? `Correct answer: ${item.expectedAnswer}`
      : 'No hidden answer key was found for this question.';
   
      const rawStatus =
  shouldGrade && studentAnswer.hasAnswer && item.expectedAnswer && !isRevealed
    ? normalizeGeminiStatus(checked.status || '')
    : shouldGrade
      ? 'incorrect'
      : 'empty';

const partialIsCorrect =
  rawStatus === 'partial' &&
  Number(checked.matchPercent || 0) >= 75 &&
  canScorePartialAsCorrect({
    questionType: item.sectionTitle,
    question: item.question,
    expectedAnswer: item.expectedAnswer,
    studentAnswer: studentAnswer.displayAnswer
  });

const status = rawStatus === 'correct' ? 'correct' : partialIsCorrect ? 'partial' : rawStatus === 'empty' ? 'empty' : 'incorrect';
const feedback = status === 'empty' ? 'Not answered.' : status === 'correct' || status === 'partial' ? '' : answerKeyFeedback;

    return {
      id: item.id,
      questionNumber: index + 1,
      worksheetQuestionNumber: item.number,
      questionType: item.sectionTitle || DEFAULT_SECTION_TITLE,
      question: item.question,
      answerMode: studentAnswer.answerMode,
      studentAnswer: studentAnswer.displayAnswer,
      attempted: studentAnswer.hasAnswer,
      usedHandwriting: Boolean(studentAnswer.drawing),
      status,
      score: status === 'correct' || status === 'partial' ? 1 : 0,
      feedback,
      rawAiStatus: rawStatus
    };
  });
}

function buildAnalysisQuestionRows(questionResults) {
  return questionResults.map((question) => ({
    questionType: question.questionType,
    question: question.question,
    status: question.status,
    isCorrect: question.status === 'correct' || question.status === 'partial'
  }));
}

function isNumericAnswer(value = '') {
  return /^[\s\d,.%/-]+$/.test(value.toString().trim());
}

function canScorePartialAsCorrect({ questionType = '', question = '', expectedAnswer = '', studentAnswer = '' }) {
  const context = `${questionType} ${question}`;

  return (
    !isNumericAnswer(expectedAnswer) &&
    !isNumericAnswer(studentAnswer) &&
    /\b(?:answer|explain|describe|why|how|reason|short|long|sentence|definition|define)\b/i.test(context)
  );
}

function buildLessonDetails(payload = {}) {
  return {
    school: payload.school || '',
    standard: payload.standard || '',
    subject: payload.subject || '',
    lessonName: payload.lessonName || '',
    topics: Array.isArray(payload.topics) ? payload.topics : payload.topics ? [payload.topics] : []
  };
}

function getUserDetails() {
  const user = auth.currentUser;

  return {
    uid: user?.uid || '',
    email: user?.email || '',
    displayName: user?.displayName || '',
    phoneNumber: user?.phoneNumber || '',
    isAnonymous: Boolean(user?.isAnonymous)
  };
}

function getResultStyle(status) {
  const styles = {
    correct: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
    incorrect: 'bg-rose-50 text-rose-700 border-rose-200',
    review: 'bg-slate-50 text-slate-700 border-slate-200',
    empty: 'bg-slate-50 text-slate-500 border-slate-200'
  };

  return styles[status] || styles.review;
}

function getResultIcon(status) {
  if (status === 'correct') return <CheckCircle2 className="w-4 h-4" />;
  if (status === 'incorrect') return <XCircle className="w-4 h-4" />;
  return <CircleAlert className="w-4 h-4" />;
}

function DrawingPad({ id, value, onChange, resetToken, disabled = false }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const width = rect.width || canvas.parentElement?.clientWidth || 640;
    const height = rect.height || 160;
    const scale = window.devicePixelRatio || 1;
    const ctx = canvas.getContext('2d');

    canvas.width = Math.max(1, Math.floor(width * scale));
    canvas.height = Math.max(1, Math.floor(height * scale));
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (value) {
      const image = new Image();
      image.onload = () => {
        ctx.drawImage(image, 0, 0, width, height);
      };
      image.src = value;
    }
  }, [value]);

  useEffect(() => {
    setupCanvas();
    window.addEventListener('resize', setupCanvas);

    return () => window.removeEventListener('resize', setupCanvas);
  }, [setupCanvas, resetToken]);

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const startDrawing = (event) => {
    if (disabled) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const point = getPoint(event);

    drawingRef.current = true;
    canvas.setPointerCapture?.(event.pointerId);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const draw = (event) => {
    if (disabled || !drawingRef.current) return;

    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const point = getPoint(event);

    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const stopDrawing = (event) => {
    if (!drawingRef.current) return;

    event.preventDefault();
    drawingRef.current = false;
    canvasRef.current?.releasePointerCapture?.(event.pointerId);
    onChange(id, canvasRef.current.toDataURL('image/png'));
  };

  const clearPad = () => {
    if (disabled) return;
    setupCanvas();
    onChange(id, '');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-bold text-slate-600">
          <PenLine className="w-3.5 h-3.5" />
          Write answer
        </span>
        <button
          type="button"
          onClick={clearPad}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[10px] sm:text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Eraser className="w-3.5 h-3.5" />
          Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        className={`h-32 sm:h-40 w-full rounded-lg border-2 border-slate-200 bg-white shadow-inner touch-none ${disabled ? 'pointer-events-none opacity-60' : ''}`}
        onPointerDown={startDrawing}
        onPointerMove={draw}
        onPointerUp={stopDrawing}
        onPointerCancel={stopDrawing}
        onPointerLeave={stopDrawing}
      />
    </div>
  );
}

function SolveWorksheetOnline() {
  const location = useLocation();
  const navigate = useNavigate();
  const cachedPayload = useMemo(() => readCachedPayload(), []);
  const routePayload = location.state || {};
  const payload = routePayload.worksheetText ? routePayload : cachedPayload;
  const worksheetText = payload.worksheetText || '';
  const worksheet = useMemo(() => buildWorksheet(worksheetText), [worksheetText]);
  const [typedAnswers, setTypedAnswers] = useState({});
  const [drawings, setDrawings] = useState({});
  const [answerModes, setAnswerModes] = useState({});
  const [results, setResults] = useState({});
  const [revealedAnswers, setRevealedAnswers] = useState({});
  const [submittedAnswers, setSubmittedAnswers] = useState({});
  const [isBatchChecking, setIsBatchChecking] = useState(false);
  const [message, setMessage] = useState('');
  const [resetToken, setResetToken] = useState(0);
  const [finalSubmission, setFinalSubmission] = useState(null);
  const isWorksheetSubmitted = Boolean(finalSubmission);
  const isCheckingAny = isBatchChecking;

  const handleTypedAnswerChange = (id, value) => {
    if (isWorksheetSubmitted || revealedAnswers[id] || submittedAnswers[id]) return;
    setTypedAnswers((prev) => ({ ...prev, [id]: value }));
    setResults((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleDrawingChange = useCallback((id, value) => {
    if (isWorksheetSubmitted || revealedAnswers[id] || submittedAnswers[id]) return;
    setDrawings((prev) => ({ ...prev, [id]: value }));
    setResults((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, [isWorksheetSubmitted, revealedAnswers, submittedAnswers]);

  const handleModeChange = (id, mode) => {
    if (isWorksheetSubmitted || revealedAnswers[id] || submittedAnswers[id]) return;
    setAnswerModes((prev) => ({ ...prev, [id]: mode }));
  };

  const resetAnswers = () => {
    setTypedAnswers({});
    setDrawings({});
    setResults({});
    setRevealedAnswers({});
    setSubmittedAnswers({});
    setMessage('');
    setFinalSubmission(null);
    setResetToken((prev) => prev + 1);
  };

  const setQuestionResult = (id, result) => {
    setResults((prev) => ({
      ...prev,
      [id]: result
    }));
  };

  const handleSubmitAnswer = (item, index) => {
    if (isWorksheetSubmitted || isBatchChecking || revealedAnswers[item.id] || submittedAnswers[item.id]) return;

    const typedAnswer = (typedAnswers[item.id] || '').trim();
    const drawing = drawings[item.id] || '';

    if (!typedAnswer && !drawing) {
      setQuestionResult(item.id, {
        status: 'empty',
        label: 'Not answered',
        feedback: 'Add an answer before submitting.'
      });
      return;
    }

    setSubmittedAnswers((prev) => ({
      ...prev,
      [item.id]: true
    }));
    setMessage(`Question ${index + 1} submitted.`);
  };

  const getAttemptedItems = () => {
    const checkItems = [];
    const reviewItems = [];
    const keyMatchedResults = {};

worksheet.questions.forEach((item, index) => {
  if (revealedAnswers[item.id]) return;
  if (!submittedAnswers[item.id]) return;

  const typedAnswer = (typedAnswers[item.id] || '').trim();

      const drawing = drawings[item.id] || '';

      if (!typedAnswer && !drawing) return;

      if (!item.expectedAnswer) {
        reviewItems.push(item);
        return;
      }

      if (typedAnswer && isTypedAnswerKeyMatch(typedAnswer, item.expectedAnswer)) {
        keyMatchedResults[item.id] = {
          status: 'correct',
          feedback: ''
        };
        return;
      }

      const mode = answerModes[item.id] || 'type';

      checkItems.push({
        id: item.id,
        number: item.number || index + 1,
        displayNumber: index + 1,
        sectionTitle: item.sectionTitle,
        question: item.question,
        expectedAnswer: item.expectedAnswer,
        typedAnswer,
        drawing: drawing && (!typedAnswer || mode === 'write') ? drawing : ''
      });
    });

    return {
      checkItems,
      reviewItems,
      keyMatchedResults
    };
  };

  const submitAndCheckAnswers = async () => {
    if (isWorksheetSubmitted || isBatchChecking) return;

    if (!worksheet.questions.length) {
      setMessage('No questions were found for this worksheet.');
      return;
    }

    if (!worksheet.hasAnswerKey) {
      setMessage('No hidden answer key was found for this worksheet.');
      return;
    }

    const { checkItems, reviewItems, keyMatchedResults } = getAttemptedItems();

    const hasSubmittedAnswer = worksheet.questions.some((item) => submittedAnswers[item.id]);

if (!hasSubmittedAnswer) {
  setMessage('Submit at least one question before checking the worksheet.');
  return;
}

    setMessage('');
    setIsBatchChecking(true);
    setResults((prev) => {
      const next = { ...prev };

      checkItems.forEach((item) => {
        next[item.id] = {
          status: 'review',
          label: 'Checking',
          feedback: 'Checking this attempted answer...'
        };
      });
      reviewItems.forEach((item) => {
        next[item.id] = {
          status: 'review',
          label: 'Needs review',
          feedback: 'No hidden answer key was found for this question.'
        };
      });
      Object.entries(keyMatchedResults).forEach(([id, result]) => {
        next[id] = {
          status: result.status,
          label: getResultLabel(result.status),
          feedback: result.feedback
        };
      });

      return next;
    });

    try {
      let checkedResults = { ...keyMatchedResults };
      let geminiResults = {};

      if (checkItems.length) {
        geminiResults = await checkAnswersWithGemini(checkItems);
      }

      if (checkItems.length && !Object.keys(geminiResults).length) {
        throw new Error('Gemini response did not include answer check results.');
      }

      checkedResults = {
        ...checkedResults,
        ...geminiResults
      };

const questionResults = buildFinalQuestionResults({
  questions: worksheet.questions,
  checkedResults,
  typedAnswers,
  drawings,
  answerModes,
  revealedAnswers,
  submittedAnswers
});

      const totalScore = questionResults.length;
      const achievedScore = questionResults.reduce((sum, question) => sum + question.score, 0);
      const incorrectCount = totalScore - achievedScore;
      const submittedAt = new Date();
      const user = getUserDetails();
      const docRef = await addDoc(collection(db, 'Online_Worksheet_Results'), {
        user_uid: user.uid,
        user_email: user.email,
        user_name: user.displayName,
        lessonDetails: buildLessonDetails(payload),
        totalQuestions: totalScore,
        correctCount: achievedScore,
        incorrectCount,
        datetimeOfTest: submittedAt.toISOString(),
        questions: buildAnalysisQuestionRows(questionResults)
      });
      const finalResults = questionResults.reduce((acc, question) => {
        acc[question.id] = {
          status: question.status,
          label: getResultLabel(question.status),
          feedback: question.feedback
        };
        return acc;
      }, {});
      const finalSubmittedAnswers = worksheet.questions.reduce((acc, question) => {
        acc[question.id] = true;
        return acc;
      }, {});

      setResults(finalResults);
      setSubmittedAnswers(finalSubmittedAnswers);
      setFinalSubmission({
        achievedScore,
        totalScore,
        resultId: docRef.id,
        datetimeOfTest: submittedAt.toISOString()
      });
      setMessage('');
    } catch (err) {
      console.error('Online worksheet submission failed:', err);
      setResults((prev) => {
        const next = { ...prev };

        checkItems.forEach((item) => {
          next[item.id] = {
            status: 'review',
            label: 'Needs review',
            feedback: 'AI could not check this answer right now.'
          };
        });

        return next;
      });
      setMessage('Could not submit and save the worksheet result right now. Please try again.');
    } finally {
      setIsBatchChecking(false);
    }
  };

  const showCorrectAnswer = (item) => {
    if (isWorksheetSubmitted) return;

    if (!worksheet.hasAnswerKey || !item.expectedAnswer) {
      setQuestionResult(item.id, {
        status: 'review',
        label: 'Needs review',
        feedback: 'No hidden answer key was found for this question.'
      });
      return;
    }

    setRevealedAnswers((prev) => ({
      ...prev,
      [item.id]: true
    }));

    setQuestionResult(item.id, {
      status: 'incorrect',
      label: 'Incorrect Answer',
      feedback: `Correct answer: ${item.expectedAnswer}`
    });
  };

  const summary = useMemo(() => {
    const values = Object.values(results);

    return {
      checked: values.length,
      correct: values.filter((result) => result.status === 'correct').length,
      partial: values.filter((result) => result.status === 'partial').length,
      incorrect: values.filter((result) => result.status === 'incorrect').length,
      submitted: Object.keys(submittedAnswers).length
    };
  }, [results, submittedAnswers]);

  if (!worksheetText) {
    return (
      <div className="min-h-screen bg-slate-50 px-3 py-6 sm:px-6">
        <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <BackHomeButton />
            <button
              type="button"
              onClick={() => navigate('/Eascode_Agent_Lesson')}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-indigo-700 sm:text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Worksheet Generator
            </button>
          </div>
          <h1 className="text-xl font-black text-slate-900 sm:text-3xl">Solve Worksheet Online</h1>
          <p className="mt-3 text-sm font-medium leading-6 text-slate-600">
            Generate a worksheet first, then open this page from the Solve Worksheet Online button.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-50 via-slate-50 to-white px-2 py-2 font-sans sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto w-full max-w-7xl rounded-[1rem] border border-white bg-white/85 p-3 shadow-2xl shadow-indigo-500/10 backdrop-blur-xl sm:rounded-[2rem] sm:p-8">
        <div className="mb-4 flex items-center justify-between gap-2 sm:mb-6">
          <div className="min-w-0 flex-1">
            <BackHomeButton backState={{ subject: payload.subject }} />
          </div>
          <div className="min-w-0 flex-[2] text-center">
            <h1 className="truncate text-sm font-black tracking-tight text-slate-950 sm:text-3xl">
              Solve Worksheet Online
            </h1>
            {(payload.subject || payload.lessonName) && (
              <p className="mt-1 truncate text-[10px] font-bold text-slate-500 sm:text-xs">
                {[payload.subject, payload.lessonName].filter(Boolean).join(' - ')}
              </p>
            )}
          </div>
          <div className="flex min-w-0 flex-1 justify-end">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold text-slate-700 shadow-sm hover:bg-slate-50 sm:px-4 sm:py-2 sm:text-sm"
            >
              <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Back
            </button>
          </div>
        </div>

        <div>

        {/*
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        
          <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-sm font-black text-slate-900 sm:text-lg">Worksheet</h2>
              {!worksheet.hasAnswerKey && (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[9px] font-bold text-amber-700 sm:text-xs">
                  No answer key detected
                </span>
              )}
            </div>
            <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 font-mono text-[10px] leading-relaxed text-slate-800 sm:p-4 sm:text-sm">
              {worksheet.questionText}
            </pre>
          </section>
          */}

          <section className="space-y-3">
            <div className="sticky top-2 z-20 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-lg shadow-slate-200/70 backdrop-blur sm:top-4 sm:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-sm font-black text-slate-900 sm:text-lg">Answers</h2>
                  <p className="mt-1 text-[10px] font-bold text-slate-500 sm:text-xs">
                    1 mark each{summary.submitted > 0 ? `, ${summary.submitted} submitted` : ''}
                  </p>
                  {summary.checked > 0 && (
                    <p className="mt-1 text-[10px] font-bold text-slate-500 sm:text-xs">
                      {summary.correct} correct, {summary.partial} close, {summary.incorrect} need correction
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={submitAndCheckAnswers}
                    disabled={isBatchChecking || isWorksheetSubmitted || !worksheet.hasAnswerKey}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-[10px] font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
                  >
                    {isBatchChecking ? <Loader2 className="h-3.5 w-3.5 animate-spin sm:h-4 sm:w-4" /> : <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                    {isBatchChecking ? 'Submitting...' : finalSubmission ? 'Submitted' : 'Submit Worksheet and Check answers'}
                  </button>
                  <button
                    type="button"
                    onClick={resetAnswers}
                    disabled={isCheckingAny}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[10px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:text-sm"
                  >
                    <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Reset
                  </button>
                </div>
              </div>
              {finalSubmission && (
                <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] font-bold text-emerald-700 sm:text-xs">
                  Thanks for submitting. Your score is {finalSubmission.achievedScore}/{finalSubmission.totalScore}.
                </p>
              )}
              {message && (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] font-bold text-amber-700 sm:text-xs">
                  {message}
                </p>
              )}
            </div>

            <div className="space-y-3">
              {worksheet.questions.map((item, index) => {
                const mode = answerModes[item.id] || 'type';
                const result = results[item.id];
                const isAnswerRevealed = Boolean(revealedAnswers[item.id]);
                const isAnswerSubmitted = Boolean(submittedAnswers[item.id]);
                const hasAttemptedAnswer = Boolean((typedAnswers[item.id] || '').trim() || drawings[item.id]);
                const isAnswerLocked = isWorksheetSubmitted || isAnswerRevealed || isAnswerSubmitted;

                return (
                  <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-[10px] font-black uppercase tracking-wide text-indigo-600 sm:text-xs">
                          Question {index + 1}
                        </p>
                        <span className="mt-1 inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-black text-slate-500 sm:text-xs">
                          1 mark
                        </span>
                        {item.sectionTitle && item.sectionTitle !== DEFAULT_SECTION_TITLE && (
                          <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-slate-400 sm:text-xs">
                            {item.sectionTitle}
                          </p>
                        )}
                        <pre className="mt-1 whitespace-pre-wrap font-sans text-xs font-semibold leading-5 text-slate-800 sm:text-sm sm:leading-6">
                          {item.question}
                        </pre>
                      </div>
                      {isAnswerSubmitted && !result && (
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-[10px] font-black text-indigo-700 sm:text-xs">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Submitted
                        </span>
                      )}
                      {result && (
                        <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-1 text-[10px] font-black sm:text-xs ${getResultStyle(result.status)}`}>
                          {getResultIcon(result.status)}
                          {result.label}
                        </span>
                      )}
                    </div>

                    <div className="mb-3 inline-flex rounded-lg bg-slate-100 p-1">
                      <button
                        type="button"
                        onClick={() => handleModeChange(item.id, 'type')}
                        disabled={isAnswerLocked || isBatchChecking}
                        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[10px] font-bold transition disabled:cursor-not-allowed disabled:opacity-50 sm:text-xs ${mode === 'type' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                      >
                        <Keyboard className="h-3.5 w-3.5" />
                        Type
                      </button>
                      <button
                        type="button"
                        onClick={() => handleModeChange(item.id, 'write')}
                        disabled={isAnswerLocked || isBatchChecking}
                        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[10px] font-bold transition disabled:cursor-not-allowed disabled:opacity-50 sm:text-xs ${mode === 'write' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
                      >
                        <PenLine className="h-3.5 w-3.5" />
                        Write
                      </button>
                    </div>

                    {mode === 'write' ? (
                      <DrawingPad
                        id={item.id}
                        value={drawings[item.id] || ''}
                        onChange={handleDrawingChange}
                        resetToken={resetToken}
                        disabled={isAnswerLocked || isBatchChecking}
                      />
                    ) : (
                      <textarea
                        value={typedAnswers[item.id] || ''}
                        onChange={(event) => handleTypedAnswerChange(item.id, event.target.value)}
                        disabled={isAnswerLocked || isBatchChecking}
                        className="h-24 w-full resize-y rounded-lg border-2 border-slate-200 bg-slate-50 p-3 text-xs font-semibold leading-5 text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-500/10 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 sm:h-28 sm:text-sm"
                        placeholder="Type answer"
                      />
                    )}

                    <div className="mt-3 flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleSubmitAnswer(item, index)}
                        disabled={isBatchChecking || isAnswerLocked || !hasAttemptedAnswer}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-[10px] font-bold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        {isAnswerSubmitted ? 'Submitted' : 'Submit question'}
                      </button>
                      <button
                        type="button"
                        onClick={() => showCorrectAnswer(item)}
                        disabled={isWorksheetSubmitted || isBatchChecking || isAnswerRevealed || !item.expectedAnswer}
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-[10px] font-bold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
                      >
                        {isAnswerRevealed ? 'Answer shown' : 'Show correct answer'}
                      </button>
                    </div>

                    {result?.feedback && (
                      <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-[10px] font-bold text-slate-600 sm:text-xs">
                        {result.feedback}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default SolveWorksheetOnline;
